import { platformService } from '../../../services/platform.service';

/**
 * Works out a preview URL that is known to load, before anything is put on screen.
 *
 * FileAttachment.ThumbnailUrl is a signed SharePoint link captured when the file was uploaded,
 * and it expires - so pointing an <img> straight at it paints the browser's broken glyph on
 * every visit to an older ticket. Instead we verify the link off-screen, ask the server for a
 * fresh one if it has died, and only then let the caller render.
 *
 * Resolved links are remembered in local storage so a reload renders immediately rather than
 * repeating the whole discovery. A remembered link that has since expired simply fails at
 * render time and the caller's onError backstop catches it, so no expiry guessing is needed.
 */

const STORAGE_KEY = 'tikit_attachment_thumbnails';
const MAX_REMEMBERED = 200;
const VERIFY_TIMEOUT_MS = 4000;

// Inline images in the activity pane are built into a markdown string, so by the time they
// reach a renderer only the src survives - no attachment id, and no way to ask for a fresh
// link. Whoever builds that markdown registers the pairing here so the renderer can recover it.
//
// Bounded, because the keys are full SharePoint URLs and this would otherwise grow for the
// whole session. A Map iterates in insertion order, so the oldest entry is the first key.
const attachmentIdBySource = new Map<string, number>();

export const registerThumbnailSource = (src: string, attachmentId: number) => {
  if (!src || !attachmentId) return;

  attachmentIdBySource.delete(src);   // re-insert so it counts as most recently used
  attachmentIdBySource.set(src, attachmentId);

  while (attachmentIdBySource.size > MAX_REMEMBERED) {
    const oldest = attachmentIdBySource.keys().next().value;
    if (oldest === undefined) break;
    attachmentIdBySource.delete(oldest);
  }
};

export const attachmentIdForSource = (src: string) => attachmentIdBySource.get(src);

type Remembered = { [attachmentId: string]: string };

const read = (): Remembered => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const write = (entries: Remembered) => {
  try {
    // Keep the store bounded; oldest insertions drop out first.
    const keys = Object.keys(entries);
    if (keys.length > MAX_REMEMBERED) {
      const trimmed: Remembered = {};
      keys.slice(keys.length - MAX_REMEMBERED).forEach(k => trimmed[k] = entries[k]);
      entries = trimmed;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage can be unavailable or full - resolution still works, just without the shortcut.
  }
};

const remember = (attachmentId: number, url: string) => {
  const entries = read();
  delete entries[attachmentId];       // re-insert so it counts as most recent
  entries[attachmentId] = url;
  write(entries);
};

export const forgetRememberedThumbnails = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { }
};

/**
 * Drops one remembered link.
 *
 * A remembered link is trusted without re-verifying - that is the point of remembering it -
 * so once it expires it would otherwise be handed out on every later visit, and the row would
 * fall back to the photo icon for good without ever asking for a fresh one. Callers forget the
 * entry when the image fails, so the next resolve goes through verification and refresh again.
 */
export const forgetThumbnail = (attachmentId: number) => {
  if (!attachmentId) return;
  const entries = read();
  if (entries[attachmentId] === undefined) return;
  delete entries[attachmentId];
  write(entries);
};

/** Resolves true only once the image has actually decoded, so a dead link never reaches the DOM. */
export const imageLoads = (url: string, timeoutMs: number = VERIFY_TIMEOUT_MS): Promise<boolean> =>
  new Promise(resolve => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();
    let settled = false;
    const settle = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      img.onload = null;
      img.onerror = null;
      resolve(loaded);
    };

    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    setTimeout(() => settle(false), timeoutMs);
    img.src = url;
  });

/**
 * Returns a preview URL that is known to load, or null if there isn't one - in which case the
 * caller should show the photo icon.
 */
export const resolveThumbnail = async (attachment: any, api?: platformService): Promise<string | null> => {
  const attachmentId = attachment?.Id;
  if (!attachmentId) return null;

  const remembered = read()[attachmentId];
  if (remembered) return remembered;

  if (await imageLoads(attachment.ThumbnailUrl)) {
    remember(attachmentId, attachment.ThumbnailUrl);
    return attachment.ThumbnailUrl;
  }

  const fresh = await (api ?? new platformService()).refreshAttachmentThumbnail(attachmentId);
  if (fresh && await imageLoads(fresh)) {
    remember(attachmentId, fresh);
    return fresh;
  }

  return null;
};
